import { createContext, useCallback, useContext, useRef } from 'react';

/**
 * The elements the guide can point at, addressed by name rather than by selector.
 *
 * A coachmark tour usually finds its targets with `document.querySelector`, which
 * makes every class name in the console load-bearing without saying so. Here a page
 * opts in explicitly — `ref={useTourAnchor('poll-present')}` — so renaming a class
 * cannot break the guide, and removing an anchor shows up in the diff as a deleted
 * line rather than as a tour that silently points at nothing.
 *
 * Ids are user-facing vocabulary (poll, screen), not the `Quiz` identifiers the
 * types keep.
 */
export type AnchorId =
  | 'workspace-switcher'
  | 'polls-create-field'
  | 'polls-create-submit'
  | 'poll-heading'
  | 'poll-add-screen'
  | 'poll-screen'
  | 'poll-theme'
  | 'poll-save'
  | 'poll-present';

export interface AnchorStore {
  register(id: AnchorId, element: HTMLElement): () => void;
  get(id: AnchorId): HTMLElement | null;
  subscribe(listener: () => void): () => void;
  /** Bumped on every registration change, so consumers can re-measure. */
  getVersion(): number;
}

/**
 * Created per provider rather than as a module singleton: a singleton would leak
 * registrations between tests, and two mounted providers would fight over one map.
 */
export function createAnchorStore(): AnchorStore {
  const elements = new Map<AnchorId, HTMLElement>();
  const listeners = new Set<() => void>();
  let version = 0;

  const emit = () => {
    version += 1;
    for (const listener of listeners) listener();
  };

  return {
    register(id, element) {
      elements.set(id, element);
      emit();
      return () => {
        // Guard against a late cleanup from an unmounting component clobbering
        // the registration a newly mounted one has already made under the same id.
        if (elements.get(id) !== element) return;
        elements.delete(id);
        emit();
      };
    },
    get: (id) => elements.get(id) ?? null,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getVersion: () => version,
  };
}

export const AnchorContext = createContext<AnchorStore | null>(null);

/**
 * Tags an element as a guide anchor. Returns a callback ref.
 *
 * Call it unconditionally at the top of a component and hold the result in a
 * variable — several of the anchored controls render inside a permission
 * ternary, and calling this inside one would be a conditional hook.
 *
 * React 18 ignores anything a callback ref returns, so the unregister function
 * is held here and run when the ref is next called with a different node.
 */
export function useTourAnchor(id: AnchorId) {
  const store = useContext(AnchorContext);
  const unregister = useRef<(() => void) | null>(null);

  return useCallback(
    (element: HTMLElement | null) => {
      unregister.current?.();
      unregister.current = null;
      if (element && store) unregister.current = store.register(id, element);
    },
    [store, id],
  );
}
